import { useMemo } from 'react';
import { useAuth } from '../hooks/useAuth';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const WATCH = `${API_BASE}/api/v1/watch`;

export type FeedStatus = 'new' | 'dismissed' | 'interested';
export type MatchType = 'auto' | 'role';

export interface FeedItem {
  id: string;
  status: FeedStatus;
  score: number;
  match_type: MatchType;
  watcher_id: string | null;
  created_at: number;
  seen_at: number | null;
  surfaced_at: number | null;
  job_id: string;
  title: string;
  location: string | null;
  country: string | null;
  employment_type: string | null;
  area: string | null;
  apply_url: string | null;
  source: string;
  posted_at: number | null;
  company_id: string;
  company_name: string;
  company_slug: string | null;
  company_domain: string | null;
  company_logo_url: string | null;
}

export interface Watcher {
  id: string;
  type: MatchType;
  label: string | null;
  query: string | null;
  filters: Record<string, any>;
  is_active: boolean;
  last_matched_at: number | null;
  created_at: number;
  updated_at: number;
}

/** A "radar" = one role watcher channel, with per-channel unread/total. */
export interface Radar {
  id: string;
  type: 'role';
  label: string | null;
  query: string | null;
  filters: Record<string, any>;
  is_active: boolean;
  last_seen_at: number | null;
  created_at: number;
  total: number;
  unread: number;
  last_item_at: number | null;
}

/** The built-in CV-based "For you" radar (auto match). */
export interface AutoRadar {
  id: string;
  is_active: boolean;
  last_seen_at: number | null;
  query?: string | null;
  filters?: Record<string, any> | null;
  total: number;
  unread: number;
  last_item_at: number | null;
}

/** Live pool stats for the Watch hero ticker. */
export interface MarketPulse {
  companies_hiring: number;
  jobs_active: number;
  jobs_new_today: number;
  companies_new_jobs_today: number;
  day_start: number;
  tz: string;
  as_of: number;
}

export interface WatcherFilters {
  employment_type?: string[];
  country?: string[];
  location?: string;
  company_type?: string;
  remote?: boolean;
  area?: string[];
}

/** A compact job card returned by the Job-Radar KI agent (chat tool result). */
export interface AgentJobCard {
  job_id: string;
  title: string | null;
  company_id: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  location: string | null;
  country: string | null;
  employment_type: string | null;
  apply_url: string | null;
  source: string | null;
  score: number | null;
}

export interface AgentChatResponse {
  response: string;
  session_id: string;
  jobs: AgentJobCard[];
  tool_results: Array<{ tool?: string; result?: string; [k: string]: any }>;
  radar_created?: boolean;
  created_radar_id?: string | null;
  interest_added?: boolean;
  blocked?: boolean;
}

export interface RadarChatMessage {
  role: 'user' | 'assistant';
  content: string;
  jobs?: AgentJobCard[];
  created_at: number;
}

/** One job exactly as it sits on screen — sent to the agent so position
 *  references ('der 3. von oben') resolve against what the user actually sees. */
export interface VisibleJob {
  job_id: string;
  company_id: string;
  title: string;
  company: string;
  location: string;
}
/** Visible jobs grouped into the same batches the UI renders, top→bottom. */
export type VisibleJobBatch = VisibleJob[];

/** Latest Vee message per thread, keyed by session_id, for list previews. */
export interface ThreadPreview {
  text: string;
  created_at: number;
}
export type ThreadPreviews = Record<string, ThreadPreview>;

/** Typed client for the consumer Watch API (admin-gated during rollout). */
export const useWatchApi = () => {
  const { apiCall } = useAuth();

  return useMemo(() => {
    const asJson = async (res: Response) => {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        if (res.status === 429) {
          try {
            const data = JSON.parse(text);
            if (data?.info) {
              const err: any = new Error(`Watch API 429: ${data.message || 'Rate limit exceeded'}`);
              err.status = 429;
              err.rateLimitInfo = { ...data.info, type: data.type };
              throw err;
            }
          } catch (e) { if ((e as any).status === 429) throw e; }
        }
        throw new Error(`Watch API ${res.status}: ${text}`);
      }
      return res.json();
    };

    return {
      getFeed: async (
        opts: {
          status?: FeedStatus;
          watcherId?: string;
          auto?: boolean;
          limit?: number;
          offset?: number;
        } = {},
      ): Promise<{ items: FeedItem[]; count: number }> => {
        const p = new URLSearchParams();
        if (opts.status) p.set('status', opts.status);
        if (opts.watcherId) p.set('watcher_id', opts.watcherId);
        if (opts.auto) p.set('auto', 'true');
        p.set('limit', String(opts.limit ?? 40));
        p.set('offset', String(opts.offset ?? 0));
        return asJson(await apiCall(`${WATCH}/feed?${p.toString()}`));
      },

      feedAction: async (
        id: string,
        action: 'dismiss' | 'interested' | 'uninterested',
      ): Promise<any> =>
        asJson(await apiCall(`${WATCH}/feed/${id}/${action}`, { method: 'POST' })),

      /** '+10 load more' — counts as one rate-limit unit (X-Load-More header). */
      loadMore: async (
        opts: { watcherId?: string; auto?: boolean } = {},
      ): Promise<{ surfaced: number }> => {
        const p = new URLSearchParams();
        if (opts.watcherId) p.set('watcher_id', opts.watcherId);
        if (opts.auto) p.set('auto', 'true');
        return asJson(
          await apiCall(`${WATCH}/feed/load-more?${p}`, {
            method: 'POST',
            headers: { 'X-Load-More': 'true' },
          }),
        );
      },

      /** Badge count of unseen ('new') feed items. Server-side this ALSO runs a
       *  throttled daily pull+promote, so polling it at login/focus advances the
       *  daily drop even when the Radar tab is never opened. */
      unseenCount: async (): Promise<{ unseen: number }> =>
        asJson(await apiCall(`${WATCH}/feed/unseen-count`)),

      // ── Job-Radar KI (AI mode) chat ─────────────────────────────────────
      /** One chat turn with Vee. session_id = thread (auto|all|interested|radarId).
       *  `visibleJobs` carries the on-screen job order (grouped into the UI's
       *  batches, top→bottom) so the agent resolves "der N. von oben" reliably. */
      radarChat: async (
        message: string,
        sessionId: string = 'auto',
        visibleJobs: VisibleJobBatch[] = [],
      ): Promise<AgentChatResponse> =>
        asJson(
          await apiCall(`${WATCH}/agent/chat`, {
            method: 'POST',
            body: JSON.stringify({ message, session_id: sessionId, visible_jobs: visibleJobs }),
          }),
        ),

      radarChatHistory: async (
        sessionId: string = 'auto',
        limit = 60,
      ): Promise<{ session_id: string; messages: RadarChatMessage[] }> =>
        asJson(
          await apiCall(
            `${WATCH}/agent/chat/history?session_id=${encodeURIComponent(sessionId)}&limit=${limit}`,
          ),
        ),

      /** Latest Vee message per thread for the conversation-list preview line. */
      radarChatPreviews: async (): Promise<{ previews: ThreadPreviews }> =>
        asJson(await apiCall(`${WATCH}/agent/chat/previews`)),

      // ── Radars (channel view) ───────────────────────────────────────────
      getRadars: async (): Promise<{ radars: Radar[]; auto: AutoRadar | null }> =>
        asJson(await apiCall(`${WATCH}/radars`)),

      /** Live pool search — bypasses agent chat for instant job cards. */
      searchJobs: async (body: {
        query: string;
        location?: string;
        remote?: boolean;
        limit?: number;
        exclude_job_ids?: string[];
        exclude_company_ids?: string[];
      }): Promise<{ jobs: AgentJobCard[]; message: string }> =>
        asJson(
          await apiCall(`${WATCH}/search`, {
            method: 'POST',
            body: JSON.stringify(body),
          }),
        ),

      /** Persist a UI-side search turn so it survives refresh + feeds context. */
      persistChatTurn: async (
        sessionId: string,
        userText: string,
        aiText: string,
        jobs: AgentJobCard[],
      ): Promise<{ ok: boolean }> =>
        asJson(
          await apiCall(`${WATCH}/agent/chat/turn`, {
            method: 'POST',
            body: JSON.stringify({
              session_id: sessionId,
              user_text: userText,
              ai_text: aiText,
              jobs,
            }),
          }),
        ),

      marketPulse: async (): Promise<MarketPulse> =>
        asJson(await apiCall(`${WATCH}/market-pulse`)),

      /** Public aggregate stats (counts only) — no auth, powers the logged-out
       *  Radar preview ticker. Plain fetch so it works without a session. */
      marketPulsePublic: async (): Promise<MarketPulse> =>
        asJson(await fetch(`${WATCH}/market-pulse`)),

      markRadarSeen: async (radarId: string): Promise<any> =>
        asJson(await apiCall(`${WATCH}/radars/${radarId}/seen`, { method: 'POST' })),

      getWatchers: async (): Promise<{ watchers: Watcher[] }> =>
        asJson(await apiCall(`${WATCH}/watchers`)),

      createWatcher: async (body: {
        query: string;
        label?: string;
        filters?: WatcherFilters;
      }): Promise<{ watcher: Watcher }> =>
        asJson(
          await apiCall(`${WATCH}/watchers`, {
            method: 'POST',
            body: JSON.stringify({ filters: {}, ...body }),
          }),
        ),

      deleteWatcher: async (id: string): Promise<any> =>
        asJson(await apiCall(`${WATCH}/watchers/${id}`, { method: 'DELETE' })),

      getAuto: async (): Promise<{ enabled: boolean }> =>
        asJson(await apiCall(`${WATCH}/auto`)),

      setAuto: async (enabled: boolean): Promise<{ enabled: boolean }> =>
        asJson(
          await apiCall(`${WATCH}/auto`, {
            method: 'POST',
            body: JSON.stringify({ enabled }),
          }),
        ),

      // ── Admin (internal panel) ──────────────────────────────────────────
      adminStats: async (): Promise<AdminStats> =>
        asJson(await apiCall(`${WATCH}/admin/stats`)),

      runIngest: async (
        opts: {
          source?: 'bundesagentur' | 'ats' | 'list' | 'big' | 'curated' | 'detect-seed' | 'detect-explore' | 'all';
          dry_run?: boolean;
          max_pages?: number;
          since_days?: number;
          limit?: number;
        } = {},
      ): Promise<any> => {
        const p = new URLSearchParams();
        if (opts.source) p.set('source', opts.source);
        if (opts.dry_run !== undefined) p.set('dry_run', String(opts.dry_run));
        if (opts.max_pages != null) p.set('max_pages', String(opts.max_pages));
        if (opts.since_days != null) p.set('since_days', String(opts.since_days));
        if (opts.limit != null) p.set('limit', String(opts.limit));
        return asJson(await apiCall(`${WATCH}/admin/run/ingest?${p}`, { method: 'POST' }));
      },

      runCleanup: async (): Promise<any> =>
        asJson(await apiCall(`${WATCH}/admin/run/cleanup`, { method: 'POST' })),

      /**
       * Liveness-Sweep: re-check every company board, confirm-before-deactivate.
       * Source-guarded + apply_url-confirmed (only 404/410 deactivate).
       */
      runRevalidate: async (
        opts: { dryRun?: boolean; limit?: number; checkLinks?: boolean } = {},
      ): Promise<any> => {
        const p = new URLSearchParams();
        p.set('dry_run', String(opts.dryRun ?? true));
        p.set('check_links', String(opts.checkLinks ?? true));
        if (opts.limit != null) p.set('limit', String(opts.limit));
        return asJson(await apiCall(`${WATCH}/admin/run/revalidate?${p}`, { method: 'POST' }));
      },

      /** Pull-model: embed unindexed jobs into the jobs Pinecone index. */
      runEmbed: async (): Promise<any> =>
        asJson(await apiCall(`${WATCH}/admin/run/embed`, { method: 'POST' })),

      /** Hard retention: keep only jobs detected within windowDays. */
      runRetention: async (windowDays = 28): Promise<any> =>
        asJson(
          await apiCall(`${WATCH}/admin/run/retention?window_days=${windowDays}`, {
            method: 'POST',
          }),
        ),

      /** DESTRUCTIVE reset (DB + Pinecone). Requires confirm=true. */
      resetData: async (
        opts: { dropWatchers?: boolean; vectors?: boolean } = {},
      ): Promise<any> => {
        const p = new URLSearchParams({ confirm: 'true' });
        if (opts.dropWatchers !== undefined) p.set('drop_watchers', String(opts.dropWatchers));
        if (opts.vectors !== undefined) p.set('vectors', String(opts.vectors));
        return asJson(await apiCall(`${WATCH}/admin/reset?${p}`, { method: 'POST' }));
      },

      adminMarqueeRegistry: async (): Promise<MarqueeRegistry> =>
        asJson(await apiCall(`${WATCH}/admin/marquee-registry`)),

      adminSearchCompanies: async (q: string): Promise<{ results: CompanySearchHit[] }> =>
        asJson(await apiCall(`${WATCH}/admin/companies/search?q=${encodeURIComponent(q)}`)),

      adminCompanyDetail: async (opts: {
        companyId?: string;
        domain?: string;
        jobLimit?: number;
        includeInactive?: boolean;
      } = {}): Promise<AdminCompanyDetail> => {
        const p = new URLSearchParams();
        if (opts.companyId) p.set('company_id', opts.companyId);
        if (opts.domain) p.set('domain', opts.domain);
        if (opts.jobLimit != null) p.set('job_limit', String(opts.jobLimit));
        if (opts.includeInactive === false) p.set('include_inactive', 'false');
        return asJson(await apiCall(`${WATCH}/admin/companies/detail?${p.toString()}`));
      },

      adminDiscoverCompany: async (body: {
        q: string; ingest?: boolean; company_name?: string; embed_after?: boolean;
      }): Promise<DiscoverResult> =>
        asJson(
          await apiCall(`${WATCH}/admin/companies/discover`, {
            method: 'POST',
            body: JSON.stringify(body),
          }),
        ),

      adminCreateManualJob: async (body: ManualJobCreate): Promise<{
        job_id: string; company_id: string; inserted: boolean; embed_started?: boolean;
      }> =>
        asJson(
          await apiCall(`${WATCH}/admin/jobs/manual`, {
            method: 'POST',
            body: JSON.stringify(body),
          }),
        ),
    };
  }, [apiCall]);
};

export interface CompanySearchHit {
  id: string;
  name: string;
  domain: string | null;
  ats_type: string | null;
  ats_slug: string | null;
  source: string | null;
  jobs_active: number;
  jobs_manual: number;
}

export interface AdminCompanyJobRow {
  id: string;
  company_id: string;
  fingerprint: string;
  source: string;
  source_job_id: string | null;
  title: string;
  location: string | null;
  country: string | null;
  remote: boolean | null;
  employment_type: string | null;
  area: string | null;
  apply_url: string | null;
  posted_at: number | null;
  first_seen_at: number;
  last_seen_at: number;
  is_active: boolean;
  matched_at: number | null;
  vector_indexed_at: number | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  salary_period: string | null;
  salary_raw: string | null;
  enrich_path: string | null;
  created_at: number;
  updated_at: number;
  description_preview: string | null;
  description_len: number | null;
  has_raw: boolean;
  role_profile_preview?: string;
  role_profile_len?: number;
}

export interface AdminCompanyDetail {
  company: Record<string, unknown>;
  job_counts: {
    jobs_total: number;
    jobs_active: number;
    jobs_inactive: number;
    jobs_ba: number;
    jobs_ats: number;
    jobs_manual: number;
    jobs_indexed: number;
    jobs_enriched: number;
  };
  jobs: AdminCompanyJobRow[];
  jobs_truncated: boolean;
}

export interface MarqueeEntry {
  name: string;
  domain: string;
  category: string;
  tier: 'tracked' | 'blocked';
  status: 'live' | 'empty' | 'not_imported' | 'not_found' | 'partial' | 'live_via_ba';
  ats_type: string | null;
  ats_slug: string | null;
  reason: string | null;
  company_id: string | null;
  company_name_db: string | null;
  jobs_active: number;
  jobs_ba: number;
  jobs_ats: number;
  jobs_manual: number;
}

export interface MarqueeRegistry {
  entries: MarqueeEntry[];
  summary: {
    tracked: number;
    blocked: number;
    live: number;
    live_via_ba: number;
    not_found: number;
    partial: number;
  };
}

export interface ManualJobCreate {
  company_id?: string;
  company_name?: string;
  domain?: string;
  title: string;
  location?: string;
  apply_url?: string;
  description?: string;
  employment_type?: string;
  embed_after?: boolean;
}

export interface DiscoverJobPreview {
  title: string;
  location: string | null;
  apply_url: string | null;
  external_id: string;
}

export interface DiscoverResult {
  query: string;
  domain: string | null;
  display_name: string;
  db_results: CompanySearchHit[];
  marquee_hint: {
    name: string;
    domain: string;
    tier: 'tracked' | 'blocked';
    ats_type: string | null;
    ats_slug: string | null;
    reason: string | null;
  } | null;
  discovery: {
    detected: boolean;
    ats_type: string | null;
    ats_slug: string | null;
    careers_url: string | null;
    confidence: number;
    notes: string | null;
    jobs_found: number;
    jobs_preview: DiscoverJobPreview[];
    fetch_error: string | null;
  };
  ingest: { company_id: string | null; new: number; updated: number; errors: number } | null;
  embed_started?: boolean;
}

export interface AdminStats {
  counts: {
    companies_discovered: number;
    jobs_total: number;
    jobs_active: number;
    jobs_inactive?: number;
    jobs_stale_active?: number;
    jobs_enriched: number;
    jobs_indexed: number;
    jobs_unmatched: number;
    feed_total: number;
    feed_new: number;
    feed_queued: number;
    feed_interested: number;
    watchers_role: number;
    auto_active: number;
  };
  jobs_by_source?: Record<string, number>;
  source_mix?: {
    companies_with_jobs?: number;
    both_ba_ats?: number;
    ats_only?: number;
    ba_only?: number;
    with_manual?: number;
    both_samples?: { name: string; domain: string | null; jobs_ba: number; jobs_ats: number }[];
  };
  pull_model?: boolean;
  coverage?: {
    target_total: number;
    covered: number;
    pct: number;
    gaps: number;
    summary: MarqueeRegistry['summary'];
    by_category: Record<string, { total: number; covered: number; gaps: number }>;
    stale_days: number;
    retention_days: number;
    registry_tracked: number;
    registry_blocked: number;
    marquee_gaps?: Array<{
      name: string;
      domain: string;
      category: string;
      status: string;
      reason?: string | null;
      tier: string;
    }>;
    seed?: {
      total_with_domain: number;
      with_ats: number;
      imported: number;
      with_jobs: number;
      pct_with_jobs: number;
      gaps: number;
      gap_sample: Array<{
        name: string;
        domain: string;
        ats_type: string;
        ats_slug: string;
      }>;
      by_ats?: Array<{ ats_type: string; pinned: number; with_jobs: number }>;
    };
    explore?: {
      total: number;
      with_website: number;
      with_domain: number;
      with_ats: number;
      with_jobs: number;
      pinned_with_jobs: number;
      pct_with_jobs: number;
      gaps: number;
      gap_sample: Array<{
        name: string;
        website: string | null;
        domain: string | null;
        ats_type: string | null;
        ats_slug: string | null;
        jobs: number;
      }>;
    };
  };
  recent_companies: { name: string; domain: string | null; discovered_at: number | null }[];
  recent_jobs: {
    title: string; source: string; employment_type: string | null;
    first_seen_at: number; company_name: string; enriched: boolean;
  }[];
  last_runs?: Record<string, {
    name: string;
    status: 'running' | 'done' | 'error';
    started_at?: number;
    finished_at?: number;
    result?: any;
    error?: string;
  }>;
}
